import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { useFormik } from 'formik';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { Button } from '@/components/Button/Button';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import {
  CREATE_API_KEY_VALIDATION,
  EDIT_API_KEY_VALIDATION,
} from '@/bundle/_MasterAdmin/_DevelopersPage/CreateApiKeyForm/const';
import { FormikDatePicker } from '@/components/form/fields/FormikDatePicker/FormikDatePicker';
import { addDays, addYears } from 'date-fns';
import { ExpirationDateLabel } from '@/bundle/_MasterAdmin/_DevelopersPage/CreateApiKeyForm/ui/ExpirationDateLabel';
import { ApiKeyType } from '@/types/sharedTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiKey, updateApiKey } from '@/bundle/_MasterAdmin/_DevelopersPage/api/api';
import { CreateApiKeyPayloadType, CreatedApiKeyType } from '@/api/v1/api-key/createApiKeyApi';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { UpdateApiKeyPayloadType } from '@/api/v1/api-key/updateApiKeyApi';
import { getFormatTimeZoneDate } from '@/helpers/dateHelpers/dateHelpers';

type CreateApiKeyFormType = {
  onClose: () => void;
  apiKey: ApiKeyType | null;
  onSuccess: (createdApiKey: CreatedApiKeyType) => void;
  isEdit: boolean;
};

const getTomorrow = () => {
  return addDays(new Date(), 1);
};

export const getDateYearFromToday = () => {
  const today = new Date();

  return addYears(today, 1);
};

export const CreateApiKeyForm = ({ onClose, apiKey, isEdit, onSuccess }: CreateApiKeyFormType) => {
  const queryClient = useQueryClient();
  const tomorrowDate = getTomorrow();
  const dateYearFromTomorrow = getDateYearFromToday();

  const validationSchema = isEdit ? EDIT_API_KEY_VALIDATION : CREATE_API_KEY_VALIDATION;
  const initialValues = isEdit ? { name: apiKey?.name } : { name: '', date_expired: dateYearFromTomorrow };

  const {
    data: createApiKeyData,
    mutate: createApiKeyMutate,
    isPending: isCreatePending,
  } = useMutation({
    mutationKey: ['create_api_key'],
    mutationFn: (payload: CreateApiKeyPayloadType) => createApiKey(payload),
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      queryClient.invalidateQueries({ queryKey: ['get_api_keys'] });

      onSuccess(response.body);
    },
  });

  const {
    data: updateApiKeyData,
    mutate: updateApiKeyMutate,
    isPending: isUpdatePending,
  } = useMutation({
    mutationKey: ['update_api_key'],
    mutationFn: (payload: UpdateApiKeyPayloadType) => updateApiKey(apiKey?.id, payload),
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      queryClient.invalidateQueries({ queryKey: ['get_api_keys'] });

      onClose();
    },
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (isEdit) {
        const payload = {
          name: values.name,
        };

        return updateApiKeyMutate(payload);
      }

      const formattedTimeZoneDate = getFormatTimeZoneDate(values.date_expired);

      const payload = {
        name: values.name,
        date_expired: formattedTimeZoneDate,
      };

      createApiKeyMutate(payload);
    },
  });

  const apiError = createApiKeyData?.error || updateApiKeyData?.error;

  return (
    <FormikForm value={formik}>
      <FormikInput name='name' placeholder='API Key Name' label='Name*' apiError={apiError} autoFocus />
      {!isEdit && (
        <FormikDatePicker
          name='date_expired'
          isClearable={false}
          minDate={tomorrowDate}
          maxDate={dateYearFromTomorrow}
          label={<ExpirationDateLabel />}
          apiError={apiError}
        />
      )}
      <ButtonGroup marginTop='14px'>
        <Button variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button type='submit' isLoading={isCreatePending || isUpdatePending}>
          Save
        </Button>
      </ButtonGroup>
    </FormikForm>
  );
};
